import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { t as translate } from "i18next";
import OkastPlayer from "okast-player";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMount } from "react-use";

import COLORS from "@/src/constants/colors";

import OfferAPI from "../../api/OfferAPI";
import { MediaType } from "../../api/OfferAPI.types";
import {
  getImageFromPictures,
  getTranslatedAttr,
} from "../../utils/data/Content";
import prettyDurations, { timecodeToDuration } from "../../utils/durations";

import type { PlayerAPI, PlayerParams, SmpteController } from "okast-player";
import type { JSX } from "react";

import type {
  GetMediaResponse,
  MediaChildren,
  MediaCuepoint,
} from "../../api/MediaAPI.types";
import type { CuepointList } from "../../api/OfferAPI.types";
import type { AppState } from "../../reducers";

const CUEPOINT_CLASSNAMES = ["cuepoint-marker"];
const CUEPOINT_CLASSNAMES_DISABLED = ["cuepoint-marker-disabled"];

export interface Marker {
  time: number;
  title: string;
  cssClasses?: string[];
}

function isGetMediaResponse(
  item: GetMediaResponse | MediaChildren | undefined,
): item is GetMediaResponse {
  return Boolean(item && "audio_only" in item);
}

export function convertCuepointsToMarkers(
  cuepointList: CuepointList,
): Marker[] {
  return cuepointList.map((timestamps: number) => ({
    cssClasses: CUEPOINT_CLASSNAMES,
    time: timestamps,
    title: translate("cuepoint", {
      ns: "editor-content",
    }),
  }));
}

export function convertMediaCuepointsToMarkers(
  mediaCuepointList: MediaCuepoint[],
): Marker[] {
  return mediaCuepointList.map((mediaCuepoint) => ({
    cssClasses: mediaCuepoint.enabled
      ? CUEPOINT_CLASSNAMES
      : CUEPOINT_CLASSNAMES_DISABLED,
    time: timecodeToDuration(mediaCuepoint.timecode).seconds,
    title: translate(
      mediaCuepoint.enabled ? "enabledCuepoint" : "disabledCuepoint",
      {
        ns: "editor-content",
      },
    ),
  }));
}

interface PlayerProps {
  item?: GetMediaResponse | MediaChildren;
  playerParams?: PlayerParams;
  isFullSize?: boolean;
  autoPlay?: boolean;
  onInit?: (player: PlayerAPI, smpteController: SmpteController | null) => void;
  className?: string;
}

export default function Player({
  item,
  playerParams,
  isFullSize,
  autoPlay,
  onInit,
  className,
}: PlayerProps): JSX.Element | null {
  const lang = useSelector((state: AppState) => state.variableReducer.lang);
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const { t } = useTranslation("editor-content");

  const getMediaUrlQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getMediaUrl,
    params: [
      {
        mediaUuid: item?.uuid ?? "",
        language: lang,
      },
    ],
  });

  const { data: mediaUrl } = useQuery({
    ...getMediaUrlQuery,
    enabled: Boolean(item?.uuid) && playerParams === undefined,
    cacheTime: 0,
  });

  const onResize = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const params: PlayerParams | undefined = useMemo(() => {
    if (playerParams) return playerParams;

    if (!mediaUrl) return undefined;

    const mediaItem = isGetMediaResponse(item) ? item : undefined;
    const isAudioOnly = mediaItem?.audio_only;

    const {
      source,
      chapters,
      type,
      subtitles,
      thumbnails,
      media_uuid: mediaUuid,
      media_type: mediaType,
      user,
    } = mediaUrl;

    const url = `${window.location.origin}/api/player_stats/v2`;
    const title = getTranslatedAttr(item, "name", lang);

    const markers: Marker[] = [
      ...(chapters ?? []),
      ...(mediaItem?.cuepoints
        ? convertMediaCuepointsToMarkers(mediaItem.cuepoints)
        : []),
    ];

    const defaultPictures = item?.translations?.find(
      (trans) => trans.default,
    )?.pictures;

    const seekbarSegments = mediaItem?.freemium_end_ms
      ? [
          {
            start: 0,
            end: prettyDurations(mediaItem.freemium_end_ms, {
              valueUnit: "ms",
            }).number.seconds,
            color: COLORS.primary.DEFAULT,
            label: t("freemium"),
          },
        ]
      : undefined;

    return {
      url,
      lang,
      type,
      source,
      markers,
      subtitles,
      thumbnails,
      editorMode: true,
      session_token: token,
      videoUuid: mediaUuid,
      title: title ?? item?.label,
      userUuid: user?.uuid,
      sourceType: isAudioOnly ? "audio" : "video",
      isLive: !!(
        mediaType === MediaType.VIDEO_LIVE || mediaType === MediaType.AUDIO_LIVE
      ),
      poster: isAudioOnly
        ? getImageFromPictures(defaultPictures, "169", "player")
        : undefined,
      seekbarSegments,
    };
  }, [item, lang, mediaUrl, playerParams, t, token]);

  useMount(() => {
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  });

  const containerClassName = classNames(
    "[&>.okast-player-audio_.okast-player_.bitmovinplayer-poster]:!block aspect-video",
    isFullSize && "size-full",
    className,
  );

  if (!params) return null;

  return (
    <div className={containerClassName}>
      <OkastPlayer
        disableEvents
        autoPlay={autoPlay}
        lang={lang}
        onInit={onInit}
        params={params}
      />
    </div>
  );
}
